<script setup>
import {ref, reactive, computed, watch, onMounted, toRaw, onBeforeUnmount, provide} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useMainOrdersStore} from '@/stores/mainOrders.js'
import {useConfigStore} from '@/stores/config.js'
import {useUserStore} from "@/stores/user.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {numeric, required} from '@vuelidate/validators';
import {useVuelidate} from '@vuelidate/core';
import Tooltip from "@/components/Tooltip/index.vue";
import Loading from "@/components/Icon/Loading.vue";
import {Icon} from "@iconify/vue";
import {showMessage} from "@/utils/showMessage.js";
import showAlert from "@/utils/showAlert.js";
import {TabGroup, TabList, Tab, TabPanels, TabPanel} from "@headlessui/vue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import Transfer from "@/views/orders/Details/Transfer.vue";
import Logs from "@/views/orders/Details/Logs.vue";
import {Button, InputGroup, InputGroupAddon, InputText, Tag, Textarea} from "primevue";
import FloatLabel from "primevue/floatlabel";
import Select from "primevue/select";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {truncateTo2} from "@/utils/truncateTo2.js";
import Users from "@/components/Users.vue";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import UserProfile from "@/components/UserProfile/index.vue";

/* tabs */
const tabs = [
  {
    title: "Details",
  },
  {
    title: "Attachments",
  },
];
const selectedTab = ref(0)
const showTransfer = ref(false)
const plusTax = ref(false)

/* attachments */
const attachments = ref(null)

/* Auxiliary keys allowed for all fields */
const helperKeys = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'Home', 'End'
]

/* ---------- Initial order values (to retrieve) ---------- */
const initialGame = ref('')
const initialServer = ref('')
const initialAccount = ref('')

/* ---------- Stores ---------- */
const route = useRoute()
const router = useRouter()
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore()
const userStore = useUserStore();
const currencyPricesStore = useCurrencyPricesStore();

/* ---------- Loading status ---------- */
const fetching = ref(true)
const loading = ref(true)
const saving = ref(false)
const deleting = ref(false)
const loadingHotOrders = ref(false)
const isUploadingAttachment = ref(false)
const orderNotFound = ref(false)

/* ---------- related to hot orders ---------- */
const isShowAddHotOrderModal = ref(false)
const isShowUserProfileModal = ref(false);
const hotOrders = ref([]);
const selectedHotOrder = ref({})
const selectedTaker = ref({})
const selectedPaymentMethod = ref({})
const currencyPrices = ref([])
const includeCurrencyPrices = ["Toman", "Instant Toman", "USDT"];

const canUpdateOrder = computed(() => userStore.hasPermission("ORDERS_CURRENCY_UPDATE") || userStore.hasPermission('BIG_BOSS'))
const canRemoveOrder = computed(() => userStore.hasPermission('ORDERS_CURRENCY_REMOVE') || userStore.hasPermission('BIG_BOSS'))
const canFinalizedOrder = computed(() => userStore.hasPermission('FINALIZE_ORDERS') || userStore.hasPermission('BIG_BOSS'))

/* ---------- The field that the user last edited. ---------- */
const activeField = ref('tomanPrice') // 'price' | 'tomanPrice' | null

/* ---------- Initial version of the order for comparison ---------- */
let initialOrder = {}

/* ---------- creation time of order ---------- */
const creationTime = ref(null)

/* ---------- Order object ---------- */
const order = reactive({
  state: '',
  game: '',
  server: '',
  account: '',
  supplier: '',
  amount: '',
  price: '',
  value: '',
  usd_tmn: '',
  tomanPrice: '',
  tomanValue: '',
  notes: '',
  id: '',
  uuid: '',
  logs: '',
  paymentMethod: '',
  takerID: '',
  finalized: null
})

const rules = computed(() => {
  return {
    state: {required},
    game: {required},
    server: {required},
    account: {required},
    amount: {required, numeric},
    price: {required, numeric},
    value: {required},
    usd_tmn: {required, numeric},
    tomanPrice: {required, numeric},
    tomanValue: {required},
    paymentMethod: {required},
  };
});
const transferRules = computed(() => {
  return {
    game: {required},
    server: {required},
    account: {required},
    amount: {required, numeric},
    price: {required, numeric},
  };
});
const $vulidate = useVuelidate(rules, order);
const $transferVulidate = useVuelidate(transferRules, order);

/* ---------- Related to state ---------- */
const isStateOpen = ref(false)
const stateDropdownRef = ref(null)
const states = ['New', 'Delivering', 'Delivered', 'Canceled', 'Paid']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}

const toggleStateDropdown = () => isStateOpen.value = !isStateOpen.value

const selectState = (state) => {
  order.state = state
  isStateOpen.value = false
}

const handleClickOutside = (e) => {
  if (stateDropdownRef.value && !stateDropdownRef.value.contains(event.target)) isStateOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

/* ---------- Select options ---------- */
const games = computed(() => configStore.getGamesByService('Currency') || [])

const selectedGame = computed(() => games.value.find(g => g.game === order.game) || null)

const servers = computed(() => {
  if (!selectedGame.value) return []

  const servers = selectedGame.value.servers

  if (!servers.includes('All')) {
    return servers
  }

  return [
    'All',
    ...servers.filter(server => server !== 'All')
  ]
})

const accounts = computed(() => {
  return order.game ? ordersStore.getGameAccountByGame(order.game) || [] : []
})

/* ---------- Selected account and its title ---------- */
const selectedAccountObj = computed(() => accounts.value.find(a => a.id === order.account) || null)

const accountTitle = computed(() => selectedAccountObj.value ? selectedAccountObj.value.title : '')

/* Digit only (no dots) */
function allowDigits(e) {
  if (
      helperKeys.includes(e.key) ||
      // Allow Ctrl+C / Ctrl+V / Ctrl+A and…:
      (e.ctrlKey || e.metaKey)
  ) return

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* Digit + a single dot  */
function allowDigitsDot(e) {
  if (
      helperKeys.includes(e.key) ||
      (e.ctrlKey || e.metaKey)
  ) return

  if (e.key === '.') {
    if (e.target.value.includes('.')) e.preventDefault()
    return
  }

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* ---------- Reaction to Game Change ---------- */
function onGameChange() {
  /* ---------- 3‑a) If the same initial Game is selected ---------- */
  if (order.game === initialGame.value) {
    // Server recovery
    if (servers.value.includes(initialServer.value)) {
      order.server = initialServer.value
    } else if (servers.value.length === 1) {
      order.server = servers.value[0]
    } else {
      order.server = ''
    }

    // Account recovery
    if (accounts.value.some(a => a.id === initialAccount.value)) {
      order.account = initialAccount.value
    } else if (accounts.value.length === 1) {
      order.account = accounts.value[0].id
    } else {
      order.account = ''
    }
    return
  }

  /* ---------- 3‑b) New Game (non-primary) ---------- */
  order.server =
      servers.value.length === 1 ? servers.value[0] : ''
  order.account =
      accounts.value.length === 1 ? accounts.value[0].id : ''
}

/* ---------- Format / Parse ---------- */
const formatText = (text) => {
  return text?.replace(/\n/g, '<br>') || ''
}

function unformatNumber(val) {
  return val.toString().replace(/,/g, '')
}

function trimDecimals(num, decimals) {
  return num.toFixed(decimals).replace(/\.?0+$/, '')
}

function formatForInput(raw) {
  if (!raw) return ''

  const parts = raw.replace(/,/g, '').split('.')
  const intPart = parts[0] === '' ? '0' : parts[0]
  const fracPart = parts[1] ?? null   // ممکن است «null» یا «» باشد

  // ویرگول برای بخش صحیح
  const intFormatted = parseInt(intPart, 10).toLocaleString('en-US')

  if (fracPart === null) return intFormatted          // اصلاً نقطه ندارد
  if (fracPart === '') return intFormatted + '.'    // نقطه ولی بدون اعشار

  return intFormatted + '.' + fracPart                // با اعشار
}


function formatNumber(val, maxDecimals = 0) {
  if (val === '' || val === null || val === undefined) return ''
  const num = Number(val.toString().replace(/,/g, ''))
  if (isNaN(num)) return ''
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  })
}


/* ---------- Inputs ---------- */
function onAmountInput(ev) {
  const cleaned = sanitizeNumber(ev.target.value)
  order.amount = cleaned
  ev.target.value = formatForInput(cleaned)   // ← نقطه به‌همراه ویرگول حفظ می‌شود
  recalc()
}

function onPriceInput(ev) {
  const cleaned = sanitizeNumber(ev.target.value)
  if (cleaned === '') {
    order.price = ''
    if (activeField.value === 'price') activeField.value = null
  } else {
    order.price = cleaned // no trimDecimals — save with all decimals
    activeField.value = 'price'
  }
  ev.target.value = formatForInput(cleaned)
  if (order.price && Number(order.usd_tmn)) {
    order.tomanPrice = (truncateTo2((Number(order.price) * Number(order.usd_tmn)))).toString()
  } else {
    order.tomanPrice = ''
  }

  recalc()
}

function onUsdInput(ev) {
  const cleaned = sanitizeNumber(ev.target.value) // Allowed point, infinite decimals
  order.usd_tmn = cleaned
  ev.target.value = formatForInput(cleaned)

  // --------‑‑‑ Synchronization dependent on activeField ‑‑‑--------
  if (activeField.value === 'tomanPrice' && order.tomanPrice && Number(order.usd_tmn)) {
    // price = tomanPrice / usd_tmn   (with up to 8 decimal places to store)
    order.price = trimDecimals(Number(order.tomanPrice) / Number(order.usd_tmn), 8)
  }

  if (activeField.value === 'price' && order.price && Number(order.usd_tmn)) {
    // tomanPrice = price * usd_tmn
    order.tomanPrice = (truncateTo2((Number(order.price) * Number(order.usd_tmn)))).toString()
  }

  recalc()
}

function onTomanPriceInput(ev) {
  const cleaned = sanitizeNumber(ev.target.value)
  if (cleaned === '') {
    order.tomanPrice = ''
    if (activeField.value === 'tomanPrice') activeField.value = null
  } else {
    order.tomanPrice = cleaned
    activeField.value = 'tomanPrice'
  }
  ev.target.value = formatForInput(cleaned)

  if (order.tomanPrice && Number(order.usd_tmn)) {
    order.price = trimDecimals(
        Number(order.tomanPrice) / Number(order.usd_tmn),
        8            // ← Maximum 8 decimal places
    )
  } else {
    order.price = ''
  }

  recalc()
}


/* ---------- Calculations ---------- */
function recalc() {
  const amt = Number(order.amount || 0)
  const prc = Number(order.price || 0)
  const usd = Number(order.usd_tmn || 0)
  const tomanPrice = Number(order.tomanPrice || 0)

  // Store the value without decimal places (numeric string)
  order.value = (amt && prc) ? (amt * prc).toString() : ''
  order.tomanValue = (amt && tomanPrice) ? (amt * tomanPrice).toFixed(0).toString() : ''
}


/*
  * Only digits and a period are allowed if needed.
  * If `maxDecimals` is 0, periods are not allowed.
 */
function sanitizeNumber(raw) {
  let s = raw.replace(/,/g, '')           // Remove commas
  // Let there be only one dot and remove all other non-numeric characters.
  s = s.replace(/[^0-9.]/g, '')

  // Only one point should be kept.
  const parts = s.split('.')
  if (parts.length > 2) {
    s = parts[0] + '.' + parts.slice(1).join('')
  }

  // No limit on the number of digits after the decimal point.
  return s
}


/* ---------- Deactivating prices ---------- */
const priceDisabled = computed(() => activeField.value === 'tomanPrice')
const tomanPriceDisabled = computed(() => activeField.value === 'price')

/* ---------- Initial fetch ---------- */
async function fetchInitialData() {
  loading.value = true
  // 1) All GameAccounts
  await ordersStore.action_getGameAccounts()

  // 2) Order details
  const data = await ordersStore.action_get_order('CurrencyBuy', route.params.id)
  if (data === false) {
    orderNotFound.value = true;
    loading.value = false
    return
  }
  Object.assign(order, data)

  hotOrders.value = data.hotOrders?.length > 0 ? data.hotOrders : []
  creationTime.value = data.creationTime;

  // Save initial values
  initialGame.value = order.game
  initialServer.value = order.server
  initialAccount.value = order.account

  // 2) Save the original full version to detect changes
  initialOrder = JSON.parse(JSON.stringify(order))

  if (data.takerID) {
    await getUser();
  }
  await getCurrencies(false);
  recalc()
  loading.value = false
}

/* ---------- Get Hot Orders ---------- */
async function getHotOrders() {
  loadingHotOrders.value = true
  const result = await ordersStore.action_getHotOrders_currencyBuy({orderID: order.id})
  if (Array.isArray(result)) {
    hotOrders.value = result
  }
  console.log(hotOrders.value)
  loadingHotOrders.value = false
}

/* ---------- Deep comparison function ---------- */
function diffDeep(curr, base) {
  if (Object.is(curr, base)) return null

  if (
      typeof curr !== 'object' || curr === null ||
      typeof base !== 'object' || base === null
  ) {
    return curr                    // Some primitive or different type
  }

  const out = Array.isArray(curr) ? [] : {}
  let hasDiff = false

  const keys = Object.keys(curr)     // Only current keys (order object)
  keys.forEach(key => {
    const sub = diffDeep(curr[key], base[key])
    if (sub !== null) {
      out[key] = sub
      hasDiff = true
    }
  })

  return hasDiff ? out : null
}

/* ---------- Detect changed fields ---------- */
function getChangedFields() {
  // Use toRaw to output Reactive Proxy
  const rawCurr = toRaw(order)
  const diff = diffDeep(rawCurr, initialOrder) || {}

  // If diff was empty, no changes
  return {id: order.id, ...diff}   // id should always be
}

/* ---------- Is there any change? ---------- */
const hasChanges = computed(() => {
  const diff = diffDeep(order, initialOrder)  // ⚠️ Without toRaw
  return diff !== null && Object.keys(diff).length > 0
})


const showStocksManagerBtn = computed(() =>
    selectedGame.value?.hasStocksManagerService === true
)

async function onSave() {
  // 1) Validation (if Vuelidate is available)
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  const changed = getChangedFields()

  if (attachments.value.length) {
    changed.attachments = {
      paymentReceipts: attachments.value
    }
  }

  // If it's just id, we haven't changed anything
  if (Object.keys(changed).length === 1) {
    showMessage('Nothing Changed', 'warning');
    return
  }

  // 3) User verification
  //const ok = await showAlert('You are updating order', 'Are you sure?', 'Yes', 'No');
  //if (!ok) return

  // 4) API call
  try {
    saving.value = true
    const res = await ordersStore.updateOrder('CurrencyBuy', changed)

    if (res === true) {
      showMessage('Order Updated', 'success');
      // After success, synchronize the initial version with the current state
      initialOrder = JSON.parse(JSON.stringify(toRaw(order)))
      ordersStore.reset_lastUpdate();
      ordersStore.reset_orders('currencyBuyOrders');
      backToOrders();
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

async function onDelete() {
  // User verification
  const ok = await showAlert('You are deleting order', 'Are you sure?', 'Yes', 'No');
  if (!ok) return

  // 4) API call
  try {
    deleting.value = true
    const res = await ordersStore.removeOrder('CurrencyBuy', {id: order.id})

    if (res === true) {
      showMessage('Order Deleted', 'success');
      ordersStore.reset_lastUpdate();
      ordersStore.reset_orders('currencyBuyOrders');
      backToOrders();
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

function backToOrders() {
  router.push({name: 'currency-buy-orders'})
}

async function onSendToStocksManager() {
  const isValid = await $transferVulidate.value.$validate(); // بهتر از $touch + $invalid

  if (!isValid) {
    return false;
  }

  plusTax.value = false;
  showTransfer.value = true;
  history.pushState({modal: true}, '') // فقط به history اضافه می‌کنه
}

async function onSendToStocksManagerPlusTax() {
  const isValid = await $transferVulidate.value.$validate(); // بهتر از $touch + $invalid

  if (!isValid) {
    return false;
  }

  plusTax.value = true;
  showTransfer.value = true;
  history.pushState({modal: true}, '') // فقط به history اضافه می‌کنه
}

function closeModal(added) {
  showTransfer.value = false
  if (history.state?.modal) {
    history.back() // فقط یه مرحله می‌ره عقب
  }
  if (added) {
    if (plusTax.value) {
      order.notes = `${order.notes}\n\nAdded to Stock Manager\nPayment Method = TOMAN`
    } else {
      order.notes = `${order.notes}\n\nAdded to Stock Manager\nPayment Method = USDT`
    }
  }
}

function onPopState() {
  if (showTransfer.value) {
    showTransfer.value = false
  }
}

const getCurrencies = async (refresh) => {
  try {
    if (!refresh) fetching.value = true
    const res = await currencyPricesStore.action_getPrices();
    if (res.result === true) {
      const tomanIndex = res.data.findIndex(item => item.name === "Toman");

      const newItem = {
        ...res.data[tomanIndex],
        name: "Instant Toman"
      };

      currencyPrices.value = [
        ...res.data.slice(0, tomanIndex + 1),
        newItem,
        ...res.data.slice(tomanIndex + 1)
      ];

      //set initial data
      selectedPaymentMethod.value = currencyPrices.value.find(item => item.name === order.paymentMethod)

      //console.log(order.paymentMethod)
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    fetching.value = false
  }
}

const onChangePaymentMethod = (paymentMethod) => {
  order.paymentMethod = paymentMethod.name
}

const getSelectedTaker = (taker) => {
  selectedTaker.value = taker || null
  order.takerID = taker ? taker.id : null
}

const getUser = async () => {
  try {
    const res2 = await userStore.action_getCompleteProfile3({id: order.takerID})
    if (res2.result === true) {
      selectedTaker.value = res2.data
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res2, life: 1500});
      showMessage(res2, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  }
}

function showUserProfileModal(user) {
  if (user.isLoading) return;
  userStore.setSelectedUser(user);
  isShowUserProfileModal.value = true
}

const changeFinalized = () => {
  if (order.finalized) {
    order.finalized = false
  } else {
    order.finalized = true
  }
}

onMounted(() => {
  window.addEventListener('popstate', onPopState)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopState)
})

onMounted(async () => {
  await fetchInitialData();
})

/* ---------- Watch for Game Change ---------- */
watch(
    () => order.game,
    () => onGameChange()
)

provide('isShowUserProfileModal', isShowUserProfileModal)
</script>

<template>
  <Transfer v-if="showTransfer" @close="closeModal" :order="order" :plusTax="plusTax"
            orderType="currencyBuy"></Transfer>
  <div v-else class="h-full">
    <TabGroup :selectedIndex="selectedTab" @change="(index)=> selectedTab = index">
      <div class="space-y-4 text-white/80 text-sm h-full">
        <!-- Loading mode -->
        <div v-if="loading" class="flex items-center justify-center h-60">
          <Icon icon="eos-icons:bubble-loading" class="size-12"/>
        </div>
        <div v-else-if="orderNotFound" class="flex flex-col gap-2 items-center justify-center h-60 text-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="2.5rem" height="2.5rem" viewBox="0 0 24 24">
            <path d="M0 0h24v24H0z" fill="none" />
            <g class="alert-outline">
              <g fill="currentColor" class="Vector">
                <path fill-rule="evenodd" d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10m-10 8a8 8 0 1 0 0-16a8 8 0 0 0 0 16" clip-rule="evenodd" />
                <path fill-rule="evenodd" d="M12 14a1 1 0 0 1-1-1V8a1 1 0 1 1 2 0v5a1 1 0 0 1-1 1" clip-rule="evenodd" />
                <path d="M11 16a1 1 0 1 1 2 0a1 1 0 0 1-2 0" />
              </g>
            </g>
          </svg>
          Order Not Found
        </div>

        <!-- Order details form -->
        <TabPanels v-else class="h-full">
          <div class="flex flex-col gap-2 h-full">
            <div class="flex w-full">
              <div class="grid grid-cols-5 gap-4 h-full w-full">
                <div class="col-span-4 flex flex-row justify-between items-center">
                  <div class="flex flex-row gap-2 items-center">
                    <Tooltip placement="top" arrow>
                      <template #button>
                        <div class="back-btn" @click="backToOrders">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                               class="w-4 h-4 2xl:w-6 2xl:h-6">
                            <path fill="currentColor"
                                  d="m7.825 13l4.9 4.9q.3.3.288.7t-.313.7q-.3.275-.7.288t-.7-.288l-6.6-6.6q-.15-.15-.213-.325T4.426 12t.063-.375t.212-.325l6.6-6.6q.275-.275.688-.275t.712.275q.3.3.3.713t-.3.712L7.825 11H19q.425 0 .713.288T20 12t-.288.713T19 13z"/>
                          </svg>
                        </div>
                      </template>
                      <span>{{ $t('message.back') }}</span>
                    </Tooltip>
                    <TabList class="">
                      <Tab
                          v-slot="{ selected }"
                          as="template"
                          v-for="(item, i) in tabs"
                          :key="i"
                      >
                        <button
                            :class="[
                            selected
                              ? 'text-white bg-primary-700'
                              : 'text-slate-500 bg-white dark:bg-slate-700 dark:text-slate-300',
                          ]"
                            class="text-xs 2xl:text-sm font-medium last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-4 2xl:px-6 py-1 2xl:py-2 transition duration-150 rounded-none first:rounded-l-2xl last:rounded-r-2xl border-r last:border-r-0 border-slate-500"
                        >
                          {{ item.title }}
                        </button>
                      </Tab>
                    </TabList>
                  </div>
                  <div class="flex flex-row items-center gap-2">
                    <!-- "Send to Stocks Manager" button -->
                    <template
                        v-if="userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('STOCKS_MANAGER_ACCESS')">
                      <div v-if="showStocksManagerBtn" class="flex flex-row items-center gap-1">
                        <button @click="onSendToStocksManager"
                                class="btn rounded-l-2xl rounded-r-none py-2 px-4 bg-slate-700 hover:bg-primary-800 text-xs font-normal">
                          Send to Stocks Manager
                        </button>
                        <button @click="onSendToStocksManagerPlusTax"
                                class="btn rounded-r-2xl rounded-l-none py-2 px-4 bg-slate-700 hover:bg-primary-800 text-xs font-normal">
                          +1% Tax
                        </button>
                      </div>
                    </template>
                    <div v-if="canFinalizedOrder" @click="changeFinalized"
                         class="flex gap-2 items-center rounded-md h-[32px] 2xl:h-[40px] text-xs 2xl:text-sm px-2 py-1 border cursor-pointer select-none"
                         :class="[order.finalized ? 'bg-primary-700 border-transparent' : 'bg-transparent border-slate-700']">
                      <div class="flex items-center size-4 border rounded-sm"
                           :class="[order.finalized ? 'border-slate-400' : 'border-slate-700']">
                        <svg v-if="order.finalized" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                             stroke-linecap="round" stroke-linejoin="round"
                             class="lucide lucide-check-icon lucide-check">
                          <path d="M20 6 9 17l-5-5"/>
                        </svg>
                      </div>
                      Finalized
                    </div>
                    <div class="inline-block w-max relative" ref="stateDropdownRef">
                      <button @click="toggleStateDropdown"
                              :class="{ '!border !border-red-300': $vulidate.state.$error }"
                              class="w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md px-2 py-1 2xl:py-2 text-left flex justify-between items-center flex-row gap-2">
                        <div class="flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full" :class="stateColorsMap[order.state]"></span>
                          {{ order.state }}
                        </div>
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </button>
                      <div v-if="isStateOpen"
                           class="absolute right-0 z-10 mt-1 w-max min-w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md shadow-lg">
                        <ul>
                          <li v-for="state in states" :key="state" @click="selectState(state)"
                              class="px-2 py-2 hover:bg-slate-800 cursor-pointer flex items-center gap-2">
                            <span class="w-2 h-2 rounded-full" :class="stateColorsMap[state]"></span>
                            {{ state }}
                          </li>
                        </ul>
                      </div>
                      <template v-if="$vulidate.state.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.state.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>

                  </div>
                </div>
                <div class="col-span-1 flex flex-row justify-between">
                  <div class="flex-1 flex flex-col gap-0.5 text-xs justify-center pl-3">
                    <span>UUID: {{ order.uuid }}</span>
                    <span>Creation Time: {{ gregorianDate(creationTime, 'DD MMM YYYY HH:mm') }}</span>
                  </div>
                  <div class="flex items-start">
                    <Logs :order="order"/>
                  </div>
                </div>
              </div>
            </div>
            <!--                        <hr class="border-0 h-0.5 bg-slate-800">-->
            <div class="flex-1 grid grid-cols-5 gap-4 h-full">
              <div class="col-span-4 flex flex-col relative h-full overflow-hidden">
                <hr class="border-0 h-0.5 bg-slate-800">
                <div class="flex flex-col gap-2 2xl:gap-4 relative h-full overflow-hidden">
                  <TabPanel :unmount="false"
                            class="col-span-4 flex-1 flex flex-col gap-4 relative h-full overflow-auto custom-scrollbar-2 pt-4">
                    <div class="flex flex-row items-start gap-4">
                      <!-- ================== Game ================== -->
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-max">
                        <FloatLabel class="w-full custom-select"
                                    :class="{ 'has-error': $vulidate.game.$error || $transferVulidate.game.$error }"
                                    variant="on">
                          <Select v-model="order.game" inputId="game" :options="games"
                                  optionLabel="game" option-value="game"
                                  overlay-class="!z-[900]"
                                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                  :class="[$vulidate.game.$error || $transferVulidate.game.$error ? '!border !border-red-300' : '!border-slate-700']"
                                  input-class="!text-xs 2xl:!text-sm"
                                  filter autoFilterFocus/>
                          <label for="game" class="!bg-slate-900">Game</label>
                        </FloatLabel>
                        <template v-if="$vulidate.game.$error || $transferVulidate.game.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.game.required.$response || !$transferVulidate.game.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>
                      <!-- ================== Server ================== -->
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel class="w-full custom-select"
                                    :class="{ 'has-error': $vulidate.server.$error || $transferVulidate.server.$error }"
                                    variant="on">
                          <Select v-model="order.server" inputId="server" :options="servers"
                                  overlay-class="!z-[900]"
                                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                  :class="[$vulidate.server.$error || $transferVulidate.server.$error ? '!border !border-red-300' : '!border-slate-700']"
                                  input-class="!text-xs 2xl:!text-sm"
                                  :virtualScrollerOptions="{ itemSize: 38 }"
                                  filter autoFilterFocus/>
                          <label for="server" class="!bg-slate-900">Server</label>
                        </FloatLabel>
                        <template v-if="$vulidate.server.$error || $transferVulidate.server.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.server.required.$response || !$transferVulidate.server.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>
                      <!-- ================== Account ================== -->
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-max">
                        <FloatLabel class="w-full custom-select"
                                    :class="{ 'has-error': $vulidate.account.$error || $transferVulidate.account.$error }"
                                    variant="on">
                          <Select v-model="order.account" inputId="account" :options="accounts"
                                  optionLabel="title" option-value="id"
                                  overlay-class="!z-[900]"
                                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                  :class="[$vulidate.account.$error || $transferVulidate.account.$error ? '!border !border-red-300' : '!border-slate-700']"
                                  input-class="!text-xs 2xl:!text-sm"
                                  :virtualScrollerOptions="{ itemSize: 32 }"
                                  filter autoFilterFocus/>
                          <label for="account" class="!bg-slate-900">Account</label>
                        </FloatLabel>
                        <template v-if="$vulidate.account.$error || $transferVulidate.account.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.account.required.$response || !$transferVulidate.account.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>
                    </div>
                    <div class="grid grid-cols-5 gap-4">
                      <!-- ================== Supplier ================== -->
                      <div v-if="order.supplier" class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on">
                          <InputText id="supplier" :value="order.supplier" autocomplete="off" readonly disabled
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700 p-filled"/>
                          <label for="supplier" class="!bg-slate-900">Supplier</label>
                        </FloatLabel>
                      </div>

                      <!-- ================== Supplier ================== -->
                      <div v-else class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <Users v-model="selectedTaker" @update:modelValue="getSelectedTaker" appendTo="body"
                               label="Supplier"
                               :required="true"/>
                      </div>

                      <!-- ================== Payment Method ================== -->
                      <div class="col-span-1 flex flex-col gap-0.5">
                        <div class="flex flex-row gap-2 text-xs 2xl:text-sm">
                          <div class="flex-1 w-full">
                            <FloatLabel variant="on" class="w-full custom-select"
                                        :class="{ 'has-error': $vulidate.paymentMethod.$error }">
                              <Select v-model="selectedPaymentMethod" input-id="paymentMethod"
                                      :options="currencyPrices.filter(item => includeCurrencyPrices.includes(item.name))"
                                      optionLabel="name" :loading="fetching"
                                      class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                      :class="[$vulidate.paymentMethod.$error ? '!border !border-red-300' : '!border-slate-700']"
                                      label-class="!text-sm !items-center !flex"
                                      @update:modelValue="onChangePaymentMethod">
                                <template #option="slotProps">
                                  <div class="flex flex-row items-center gap-4 justify-between w-full">
                                    <span class="text-sm">{{ slotProps.option.name }}</span>
                                  </div>
                                </template>
                              </Select>
                              <label for="paymentMethod" class="!bg-slate-900"
                                     :class="{ 'has-error': $vulidate.paymentMethod.$error }">Payment Method</label>
                            </FloatLabel>
                          </div>
                          <button @click="getCurrencies(false)">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                 fill="none"
                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                 class="lucide lucide-refresh-cw-icon lucide-refresh-cw">
                              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                              <path d="M21 3v5h-5"/>
                              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                              <path d="M8 16H3v5"/>
                            </svg>
                          </button>
                        </div>
                        <template v-if="$vulidate.paymentMethod.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.paymentMethod.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>


                      <div v-if="selectedTaker?.id"
                           class="col-span-5 flex flex-col w-full gap-2 border border-slate-700 rounded-md p-2">
                        <div class="flex flex-row gap-2 w-full items-center">
                          <div @click.stop="showUserProfileModal(selectedTaker)" class="cursor-zoom-in">
                            <img v-if="selectedTaker.discordAvatar" :src="selectedTaker.discordAvatar"
                                 alt="avatar" class="size-10 rounded-full border border-slate-700">
                            <div v-else
                                 class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                   stroke-linejoin="round" class="lucide lucide-user-icon lucide-user">
                                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                              </svg>
                            </div>
                          </div>
                          <div class="flex-1 grid grid-cols-2 gap-1">
                            <div class="flex flex-col">
                              <div class="flex flex-row gap-2 items-center">
                                <div>{{ selectedTaker.name }} &nbsp &nbsp</div>
                              </div>
                              <div class="flex flex-row w-max text-xs text-slate-400">
                                <div>{{ selectedTaker.discordUsername ?? "-" }}</div>
                                <div>{{ "&nbsp- " + selectedTaker.discordDisplayName ?? "-" }}</div>
                              </div>
                            </div>
                            <div class="flex flex-col">
                              <div>{{ selectedTaker.verificationState ?? "-" }}</div>
                              <div>{{ discordRegistrationTime(selectedTaker.discordID) }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- ================== Amount ================== -->
                      <div class="col-span-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on">
                          <InputText id="amount" v-model="order.amount" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error': $vulidate.amount.$error || $transferVulidate.amount.$error }"
                                     @keydown="allowDigitsDot"
                                     @input="onAmountInput"/>
                          <label for="amount" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.amount.$error || $transferVulidate.amount.$error }">Amount</label>
                        </FloatLabel>
                        <template v-if="$vulidate.amount.$error || $transferVulidate.amount.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.amount.required.$response || !$transferVulidate.amount.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.amount.numeric.$response || !$transferVulidate.amount.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>

                      <!-- ================== Price per unit (USD) ================== -->
                      <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on"
                                    :class="{ 'has-error': $vulidate.price.$error || $transferVulidate.price.$error }">
                          <InputText id="price" v-model="order.price" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error has-addon': $vulidate.price.$error || $transferVulidate.price.$error }"
                                     :readonly="priceDisabled"
                                     @click="activeField = 'price'"
                                     @keydown="allowDigitsDot"
                                     @input="onPriceInput"/>
                          <label for="price" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.price.$error || $transferVulidate.price.$error }">Price/Unit</label>
                        </FloatLabel>
                        <template v-if="$vulidate.price.$error || $transferVulidate.price.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.price.required.$response || !$transferVulidate.price.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.price.numeric.$response || !$transferVulidate.price.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>

                      <!-- ================== Value (USD) ================== -->
                      <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on">
                            <InputText id="value" v-model="order.value" :value="formatNumber(order.value, 2)"
                                       autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       disabled/>
                            <label for="value" class="!bg-slate-900">Value</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px]">$
                          </InputGroupAddon>
                        </InputGroup>
                      </div>

                      <!-- ================== USD/TMN ================== -->
                      <div class="col-span-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on"
                                    :class="{ 'has-error': $vulidate.usd_tmn.$error }">
                          <InputText id="usd_tmn" v-model="order.usd_tmn" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error has-addon': $vulidate.usd_tmn.$error }"
                                     @keydown="allowDigitsDot"
                                     @input="onUsdInput"/>
                          <label for="usd_tmn" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.usd_tmn.$error }">USD/TMN</label>
                        </FloatLabel>
                        <template v-if="$vulidate.usd_tmn.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.usd_tmn.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.usd_tmn.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>
                      <!-- ================== Toman Price per unit ================== -->
                      <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on"
                                    :class="{ 'has-error': $vulidate.tomanPrice.$error }">
                          <InputText id="tPrice" v-model="order.tomanPrice" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error has-addon': $vulidate.tomanPrice.$error }"
                                     :readonly="tomanPriceDisabled"
                                     @click="activeField = 'tomanPrice'"
                                     @keydown="allowDigitsDot"
                                     @input="onTomanPriceInput"/>
                          <label for="tPrice" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.tomanPrice.$error }">Toman Price/Unit</label>
                        </FloatLabel>
                        <template v-if="$vulidate.tomanPrice.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.tomanPrice.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.tomanPrice.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>

                      <!-- ================== Toman Value ================== -->
                      <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on">
                            <InputText id="tValue" v-model="order.tomanValue" :value="formatNumber(order.tomanValue, 0)"
                                       autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       disabled/>
                            <label for="tValue" class="!bg-slate-900">Toman Value</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px]">Tmn
                          </InputGroupAddon>
                        </InputGroup>
                      </div>
                    </div>
                  </TabPanel>
                  <TabPanel :unmount="false"
                            class="flex-1 flex flex-col gap-6 relative h-full overflow-auto px-2 custom-scrollbar-2 pt-4">
                    <!-- Only changes -->
                    <Attachments
                        id="editOrder"
                        :count="16"
                        :backend-images="order.attachments?.paymentReceipts ?? []"
                        :only-changes="true"
                        v-model:uploading="isUploadingAttachment"
                        @update:payload="val => (attachments = val)"
                    />
                  </TabPanel>
                </div>
              </div>
              <div class="col-span-1 flex flex-col h-full overflow-hidden">
                <hr class="border-0 h-0.5 bg-slate-800">
                <div class="flex flex-col h-full overflow-hidden gap-4 pt-4">
                  <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm h-full">
                    <FloatLabel variant="on" class="h-full">
                      <Textarea id="notes" v-model="order.notes" rows="5" style="resize: none"
                                class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 h-full"/>
                      <label for="notes" class="!bg-slate-900">Notes</label>
                    </FloatLabel>
                  </div>
                  <hr v-if="canRemoveOrder || canUpdateOrder" class="border-0 h-0.5 bg-slate-800">
                  <div v-if="canRemoveOrder || canUpdateOrder" class="grid gap-2"
                       :class="[canRemoveOrder && canUpdateOrder ? 'grid-cols-2' : 'grid-cols-1']">
                    <button v-if="canRemoveOrder"
                            @click="onDelete" class="btn btn-outline-dark py-2 text-xs 2xl:text-sm">
                      <span v-if="loading"
                            class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                      </span>
                      Delete order
                    </button>

                    <!--        :disabled="!hasChanges || saving"-->
                    <button v-if="canUpdateOrder" @click="onSave"
                            class="btn btn-outline-primary py-2 text-xs 2xl:text-sm" :disabled="saving">
                      <span v-if="saving"
                            class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                        <Loading/>
                      </span>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanels>
      </div>
    </TabGroup>
    <UserProfile v-if="isShowUserProfileModal" :userID="userStore.selectedUser.id"/>
  </div>
</template>

<style scoped>
.input {
  @apply w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring focus:border-blue-500;
}

.back-btn {
  @apply h-6 2xl:h-8 w-6 2xl:w-8 bg-slate-200 dark:bg-slate-700 dark:text-slate-200 text-slate-600 flex flex-col justify-center items-center text-base rounded-full cursor-pointer;
}

:deep(.p-floatlabel:has(input.has-error) label) {
  --p-floatlabel-focus-color: rgb(252, 165, 165);
}

:deep(.p-floatlabel:has(input.has-error)) {
  @apply !border !border-red-300 !rounded-md
}

:deep(.p-inputgroup .p-floatlabel.has-error) {
  @apply !border-r-0 !rounded-r-none
}

:deep(.p-inputgroup:has(.p-floatlabel.has-error) .p-inputgroupaddon) {
  @apply !border !border-red-300 !rounded-md !border-l-0 !rounded-l-none
}


:deep(.p-select-clear-icon) {
  @apply size-3
}

:deep(.p-select-dropdown) {
  @apply w-6
}


/*------------*/
/*:deep(.custom-select.p-floatlabel label) {
  top: 0 !important;
  transform: translateY(-50%);
  @apply bg-slate-900 py-0 px-[0.125rem];
}*/

/*:deep(.p-floatlabel.custom-select label) {
  font-size: 0.75rem
}*/

:deep(.p-floatlabel.custom-select.has-error label) {
  /*--p-floatlabel-color: rgb(252, 165, 165);*/
  --p-floatlabel-focus-color: rgb(252, 165, 165);
}


/*:root {
  --p-select-option-focus-background: #521111;
  --p-select-option-focus-color: #707007;
}*/
</style>
